import { Component, Input, OnInit, SimpleChanges } from "@angular/core";
import { PageStateModel } from "../../types/page-state-model";
import { ActionState, TVDOpportunityModel } from "../../types/action-center-types";
import { TVDService } from "src/app/features/total-value-delivery/services/tvd.service";
import { round } from "lodash";
import { eroiIds } from "src/app/features/total-value-delivery/types/tvd-types";
import { TVDModel } from "src/app/features/total-value-delivery/types/tvd-model";

@Component({
    selector: "app-actions-header",
    templateUrl: "./actions-header.component.html",
    styleUrls: ["./actions-header.component.scss"],
    standalone: false
})
export class ActionsHeaderComponent implements OnInit {

  @Input() currPageState: ActionState | undefined;
  @Input() pageData: PageStateModel | undefined;

  // #region loaders
  loadingSales: boolean = true;
  loadingTVD: boolean = true;
  // #endregion

  noData: boolean = false;

  //#region  data points for the header
  totalOpportunitySales: number = 0; // total opportunity sales
  totalOpportunityTVD: number = 0; // opportunity - total TVD
  totalCurrentTVD: number = 0; // current - total TVD
  opportunityTVD: TVDOpportunityModel | undefined; // opportunity - TVD Opportunity By Metric
  tvdModel: TVDModel | undefined; // TVD response from the API
  currentTVD: TVDOpportunityModel | undefined; // current - TVD Opportunity By Metric
  tvdDeltaPercent: number = 0; // TVD delta percentage
  //#endregion

  constructor(private tvdService: TVDService) { }

  ngOnInit(): void {
    // subscribe to tvd data
    this.tvdService.tvdObservable.subscribe(async (t) => {
      this.tvdModel = t;
    });
    this.setUpData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageData']) {
      this.setUpData();
    }
  }

  private setUpData() {
    if (this.pageData && !this.pageData.isEmpty()) {
      this.noData = false;
      this.setUpSalesData();
      this.setUpTVDBoxData();
    } else {
      this.noData = true;
      this.loadingSales = false;
      this.loadingTVD = false;
    }
  }

  private async setUpTVDBoxData() {
    if (this.pageData && !this.pageData.isEmpty() && this.pageData.opportunityTVD) {
      this.loadingTVD = true;
      await this.setUpCurrentTVD();
      this.opportunityTVD = this.pageData.opportunityTVD
      this.totalOpportunityTVD = round(this.pageData.totalTVDOpportunity, 2);
      this.tvdDeltaPercent = this.totalCurrentTVD != 0 ? round(this.totalOpportunityTVD * 100 / this.totalCurrentTVD, 2) : 0;
      this.loadingTVD = false;
    }
  }

  async setUpCurrentTVD() {
    // Make request to tvd endpoint, set tvd data to default model if there is no tvd data
    if (this.tvdModel == undefined || this.tvdModel.tvdMetricSelections == undefined || this.tvdModel.addedMetrics == undefined) {
      // Sets the tvd observable to the default tvd model which we have subscribed to above
      await this.tvdService.setToDefaultTVDModel();
    }
    if (this.tvdModel && this.tvdModel.tvdResponse) {
      // individual metrics TVD
      let defaultIndivMetrics: {
        'id': string,
        'valueContribution': string,
        'monetaryContribution': string | undefined
      }[] = this.tvdModel.tvdResponse.filter((res) => res.id === "default_tvd").map(x => x.indvMetrics).flat();

      // total value delivered, total sales
      let defaultSummaryMetrics: {
        'id': string,
        'valueContribution': string,
        'monetaryContribution': string | undefined
      }[] = this.tvdModel.tvdResponse.filter((res) => res.id === "default_tvd").map(x => x.summMetrics).flat();

      // convert the metrics to TVDOpportunityModel , id is the key
      this.currentTVD = {
        "Productivity": this.findMetricCost(defaultIndivMetrics, eroiIds.PRODUCTIVITY).toString(),
        "Assets": (this.findMetricCost(defaultIndivMetrics, 'repair') + this.findMetricCost(defaultIndivMetrics, 'replacement')).toString(),
        "Employee Safety": this.findMetricValue(defaultIndivMetrics, eroiIds.EMPLOYEESAFETY).toString(),
        "Water": this.findMetricCost(defaultIndivMetrics, eroiIds.WATER).toString(),
        "Energy": this.findMetricCost(defaultIndivMetrics, eroiIds.ENERGY).toString(),
        "Greenhouse Gases": this.findMetricCost(defaultIndivMetrics, eroiIds.GREENHOUSEGASES).toString(),
        "Plastic Waste": this.findMetricCost(defaultIndivMetrics, eroiIds.PLASTIC).toString(),
        "Cardboard Waste": this.findMetricCost(defaultIndivMetrics, eroiIds.CARDBOARD).toString(),
        "Cost Avoidance": this.findMetricCost(defaultIndivMetrics, eroiIds.COSTAVOIDANCE).toString()
      };

      // set the current total sales
      this.totalCurrentTVD = round(this.findMetricCost(defaultSummaryMetrics, eroiIds.TOTALSALES), 2);
    }
  }

  private async setUpSalesData() {
    if (this.pageData) {
      this.loadingSales = true;
      this.totalOpportunitySales = round(this.pageData.actions.reduce((acc, curr) => acc + curr.groupedSalesOpportunity, 0), 2);
      this.loadingSales = false;
    }
  }

  removeCommas(value: string): string {
    return value.replace(/,/g, '');
  }

  findMetricCost(
    metrics: {
      'id': string,
      'valueContribution': string,
      'monetaryContribution': string | undefined
    }[], id: string): number {
    return parseFloat(this.removeCommas(metrics.find((item) => item.id == id)?.monetaryContribution ?? '0'));
  }

  findMetricValue(
    metrics: {
      'id': string,
      'valueContribution': string,
      'monetaryContribution': string | undefined
    }[], id: string): number {
    return parseFloat(this.removeCommas(metrics.find((item) => item.id == id)?.valueContribution ?? '0'));
  }
}